import { ArrowsDownUp, Tag as TagIcon } from "@phosphor-icons/react";
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { Button } from "common/components/ui/Button";
import { Input } from "common/components/ui/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";

export default function Variable({
  node,
  updateAttributes,
  editor,
}: NodeViewProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(editor.isEditable);

  const { isCustomVar, label, fallback } = node.attrs;

  function onSave() {
    if (inputRef.current) {
      updateAttributes({
        fallback: inputRef.current.value.trim(),
      });
    }
    setIsOpen(false);
  }

  useEffect(() => {
    function onUpdate() {
      setIsEditable(editor.isEditable);
    }

    editor.on("update", onUpdate);

    return () => {
      // Clean up function
      editor.off("update", onUpdate);
    };
  }, [editor]);

  return (
    <NodeViewWrapper className="inline-flex">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger
          disabled={!isEditable}
          className={clsx(
            "inline-flex h-5 shrink-0 items-center justify-center gap-0.5 text-nowrap rounded-md px-0.5 text-button-sm transition-[background] ease-in-out",

            isCustomVar
              ? `text-magenta-700 ${isOpen ? "bg-magenta-200" : "bg-magenta-100"} ${isEditable ? "hover:bg-magenta-200" : ""}`
              : `text-blue-700 ${isOpen ? "bg-blue-200" : "bg-blue-100"} ${isEditable ? "hover:bg-blue-200" : ""}`,
          )}
        >
          {!!fallback && <ArrowsDownUp size={16} />}
          {"{{"} {label} {"}}"}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-aboveDialog flex flex-col gap-y-1.5 border border-black-200 p-2"
        >
          <Input
            ref={inputRef}
            type="text"
            className="w-full"
            leftComponent={<TagIcon weight="fill" />}
            placeholder="Enter fallback value"
            defaultValue={fallback}
          />

          <span className="text-xs text-black-500">
            If the variable is missing and has no fallback, the entire message
            will be skipped
          </span>

          <Button size="sm" onClick={onSave} className="ml-auto">
            Save
          </Button>
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
}
